// FLAGS: none
// "Spaces Schedules" as a list: one ISO week per page (?week=), each day's sessions with their sign-up or
// withdraw action. A title filter (?title=) searches every week and lists the matches under their weeks.
import { addDays, format, parseISO } from "date-fns";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { ResponseConveneHostableSessionResponse } from "@src/api/v1/data-contracts";
import { DateFnslocaleMapper } from "@src/DateLocaleSetup";
import HostSignupDays from "@src/views/HostSignups/HostSignupDays";
import {
  HostWeek,
  groupByWeek,
  matchingTitle,
} from "@src/views/HostSignups/hostWeeks";

interface HostSignupsWeekListProps {
  sessions: ResponseConveneHostableSessionResponse[];
  busy: boolean;
  /** Shown as a pill on each session when the type filter is "All". */
  typeLabel?: string;
  onSignUp: (sessionId: string) => void;
  onWithdraw: (sessionId: string) => void;
}

const weekButton = "rounded-md px-3 py-1 text-sm font-medium";

const HostSignupsWeekList = ({
  sessions,
  busy,
  typeLabel = undefined,
  onSignUp,
  onWithdraw,
}: HostSignupsWeekListProps) => {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const title = searchParams.get("title") ?? "";
  const searching = title.trim() !== "";
  const weeks = groupByWeek(sessions);
  const index = Math.max(
    0,
    weeks.findIndex((week) => String(week.number) === searchParams.get("week")),
  );
  const current = weeks[index];
  const matches = weeks
    .map((week) => ({ ...week, sessions: matchingTitle(week.sessions, title) }))
    .filter((week) => week.sessions.length > 0);
  const matchCount = matches.reduce(
    (sum, week) => sum + week.sessions.length,
    0,
  );

  const update = (changes: Record<"week" | "title", string | null>) => {
    const params = new URLSearchParams(searchParams);
    Object.entries(changes).forEach(([key, value]) =>
      value ? params.set(key, value) : params.delete(key),
    );
    setSearchParams(params, { replace: true });
  };
  // Picking a week while searching leaves the search for that week's full list.
  const goTo = (position: number) =>
    update({ week: String(weeks[position].number), title: null });

  const locale = DateFnslocaleMapper();
  const range = (week: HostWeek) => {
    const monday = parseISO(week.monday);
    return `${format(monday, "d MMM", { locale })} – ${format(addDays(monday, 6), "d MMM", { locale })}`;
  };
  const days = (list: ResponseConveneHostableSessionResponse[]) => (
    <HostSignupDays
      sessions={list}
      busy={busy}
      typeLabel={typeLabel}
      onSignUp={onSignUp}
      onWithdraw={onWithdraw}
    />
  );

  if (!current) return null;

  return (
    <div>
      <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
        <input
          type="search"
          aria-label={t("HostSignups.List.FilterByTitle")}
          placeholder={t("HostSignups.List.FilterByTitle")}
          value={title}
          onChange={(event) =>
            update({ title: event.target.value, week: String(current.number) })
          }
          className="w-full max-w-xs rounded-lg border-2 border-slate-200 bg-white px-3 py-1.5 text-sm text-indigo-800 focus:border-green-500 focus:outline-none"
        />
        <nav
          aria-label={t("HostSignups.List.Weeks")}
          className="flex flex-wrap items-center gap-1"
        >
          {!searching && (
            <button
              type="button"
              className={`${weekButton} text-slate-700 disabled:opacity-40`}
              aria-label={t("HostSignups.List.PreviousWeek")}
              disabled={index === 0}
              onClick={() => goTo(index - 1)}
            >
              ‹
            </button>
          )}
          {weeks.map((week, position) => {
            const active = !searching && position === index;
            return (
              <button
                key={week.monday}
                type="button"
                title={range(week)}
                aria-current={active ? "page" : undefined}
                onClick={() => goTo(position)}
                className={`${weekButton} ${active ? "bg-indigo-800 text-white" : "bg-slate-100 text-slate-700 hover:text-indigo-800"}`}
              >
                {searching
                  ? t("HostSignups.List.WeekWithCount", {
                      number: week.number,
                      count: matchingTitle(week.sessions, title).length,
                    })
                  : t("HostSignups.List.Week", { number: week.number })}
              </button>
            );
          })}
          {!searching && (
            <button
              type="button"
              className={`${weekButton} text-slate-700 disabled:opacity-40`}
              aria-label={t("HostSignups.List.NextWeek")}
              disabled={index === weeks.length - 1}
              onClick={() => goTo(index + 1)}
            >
              ›
            </button>
          )}
        </nav>
      </div>

      {searching ? (
        <>
          <p className="text-sm text-slate-700">
            {matchCount === 0
              ? t("HostSignups.List.NoMatches", { title: title.trim() })
              : t("HostSignups.List.SearchSummary", {
                  count: matchCount,
                  title: title.trim(),
                })}
          </p>
          {matches.map((week) => (
            <section
              key={week.monday}
              aria-label={t("HostSignups.List.Week", { number: week.number })}
              className="mt-4"
            >
              <h2 className="text-sm font-semibold text-indigo-800">
                {t("HostSignups.List.Week", { number: week.number })} ·{" "}
                {range(week)}
              </h2>
              {days(week.sessions)}
            </section>
          ))}
        </>
      ) : (
        <>
          <p className="text-sm text-slate-700">
            {t("HostSignups.List.WeekSummary", {
              number: current.number,
              range: range(current),
              count: current.sessions.length,
            })}
          </p>
          {days(current.sessions)}
        </>
      )}
    </div>
  );
};

export default HostSignupsWeekList;
